import React, { useCallback, useState, useEffect } from "react";
import {
  GoogleMap,
  Polyline,
  LoadScript,
  OverlayView,
} from "@react-google-maps/api";
import resMapIcon from "@/assets/residence_map_icon.png";
import Guide from "@/assets/guide.png";
import PlaceIcon from "@mui/icons-material/Place";

interface Coordinates {
  residence: google.maps.LatLngLiteral;
  guide: google.maps.LatLngLiteral;
}

const MapContainer: React.FC<{
  coordinates: Coordinates;
  eventDetail: any;
}> = ({ coordinates, eventDetail }) => {
  const mapApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  
  const midpoint = {
    lat: (coordinates.residence.lat + coordinates.guide.lat) / 2,
    lng: (coordinates.residence.lng + coordinates.guide.lng) / 2,
  };

  const ImageHandler = (link: any) => {
    let text = link;
    text = text.replace("file/d/", "uc?id=");
    text = text.split("/view?usp=");
    return text[0];
  };

  // Callback appelé quand la map est chargée
  const onLoad = useCallback((mapInstance: google.maps.Map) => {
    console.log("[MapContainer] Map loaded");
    setMap(mapInstance);
    setIsMapLoaded(true);
    
    // Forcer un resize après un court délai pour s'assurer que le conteneur est visible
    setTimeout(() => {
      if (mapInstance) {
        google.maps.event.trigger(mapInstance, 'resize');
        mapInstance.setCenter(midpoint);
        console.log("[MapContainer] Map resized and centered");
      }
    }, 100);
  }, [midpoint]);

  // Cleanup
  const onUnmount = useCallback(() => {
    setMap(null);
    setIsMapLoaded(false);
  }, []);

  // Effet pour redimensionner la map si le conteneur change de taille
  useEffect(() => {
    if (map && isMapLoaded) {
      const handleResize = () => {
        google.maps.event.trigger(map, 'resize');
        map.setCenter(midpoint);
      };
      
      window.addEventListener('resize', handleResize);
      
      // Déclencher un resize initial après un délai
      const timeoutId = setTimeout(handleResize, 500);
      
      return () => {
        window.removeEventListener('resize', handleResize);
        clearTimeout(timeoutId);
      };
    }
  }, [map, isMapLoaded, midpoint]);

  return (
    <div style={{ marginTop: 30, marginBottom: 30, height: 300, minHeight: 300 }}>
      <LoadScript googleMapsApiKey={mapApiKey!}>
        <GoogleMap
          mapContainerStyle={{ width: "100%", height: "270px" }}
          center={midpoint}
          zoom={2.7}
          onLoad={onLoad}
          onUnmount={onUnmount}
        >
          <Polyline
            path={[coordinates.residence, coordinates.guide]}
            options={{
              strokeColor: "#4285F4", // Blue stroke color
              strokeOpacity: 1,
              strokeWeight: 5,
            }}
          />
          <OverlayView
            position={coordinates.guide}
            mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
            getPixelPositionOffset={(width, height) => ({
              x: -(width / 2),
              y: -height,
            })}
          >
            <div className="marker-containerStyle">
              <img
                src={
                  eventDetail?.guide_picture &&
                  eventDetail?.guide_picture.includes("http")
                    ? ImageHandler(eventDetail.guide_picture)
                    : Guide
                }
                alt="Guide"
              />
            </div>
          </OverlayView>

          <OverlayView
            position={coordinates.residence}
            mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
            getPixelPositionOffset={(width, height) => ({
              x: -(width / 2),
              y: -height,
            })}
          >
            <div className="marker-containerStyle">
              <img src={resMapIcon} alt="Residence" />
            </div>
          </OverlayView>

          {/* Marker for residence location */}
          {/* <Marker
            position={coordinates.residence}
            icon={{
              url: resMapIcon, // Icon for residence location
            }}
          /> */}
        </GoogleMap>
      </LoadScript>
    </div>
  );
};

export default MapContainer;
